import { useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Rect, Text, Ellipse, Group, Image as KonvaImage, Transformer } from "react-konva";
import useImage from "use-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  TextT, Square, Circle, Minus, Image as ImageIcon, Trash, ArrowUp, ArrowDown,
  FloppyDisk, Export, X, CaretLeft, UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PLATFORM_SIZES, PRESETS, DEFAULT_BRAND, buildPreset, uid, brandSwatches, rescaleLayer } from "./presets";

const FONT_WEIGHTS = [
  { v: "300", label: "Light" },
  { v: "400", label: "Regular" },
  { v: "600", label: "Semibold" },
  { v: "700", label: "Bold" },
  { v: "800", label: "Extrabold" },
  { v: "900", label: "Black" },
];

function BgImage({ url, w, h }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  const scale = Math.max(w / img.width, h / img.height);
  const cw = w / scale, ch = h / scale;
  return (
    <KonvaImage
      image={img}
      x={0}
      y={0}
      width={w}
      height={h}
      crop={{ x: (img.width - cw) / 2, y: (img.height - ch) / 2, width: cw, height: ch }}
      listening={false}
    />
  );
}

function OverlayImage({ layer, registerRef, onSelect, onChange }) {
  const [img] = useImage(layer.src, "anonymous");
  return (
    <KonvaImage
      id={layer.id}
      ref={registerRef}
      image={img}
      x={layer.x}
      y={layer.y}
      width={layer.width}
      height={layer.height}
      opacity={layer.opacity ?? 1}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={(e) => onChange({ x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
      onTransformEnd={(e) => {
        const node = e.target;
        const sx = node.scaleX(), sy = node.scaleY();
        node.scaleX(1); node.scaleY(1);
        onChange({ x: Math.round(node.x()), y: Math.round(node.y()), width: Math.max(8, Math.round(node.width() * sx)), height: Math.max(8, Math.round(node.height() * sy)) });
      }}
    />
  );
}

export default function CanvasEditor({ api, brand, initialDesign, onClose, onSaved }) {
  const b = useMemo(() => ({ ...DEFAULT_BRAND, ...(brand || {}) }), [brand]);
  const [platform, setPlatform] = useState(initialDesign?.platform || "1:1");
  const [doc, setDoc] = useState(
    initialDesign?.canvas_json || buildPreset("clean_professional", b, PLATFORM_SIZES["1:1"])
  );
  const [name, setName] = useState(initialDesign?.name || "Untitled composition");
  const [designId, setDesignId] = useState(initialDesign?.id || null);
  const [selectedId, setSelectedId] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState("background"); // background | overlay
  const [mediaList, setMediaList] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [savedMedia, setSavedMedia] = useState(null);
  const [postForm, setPostForm] = useState({ platform: "instagram", scheduled_date: "", caption: "" });
  const [postCreating, setPostCreating] = useState(false);

  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const trRef = useRef(null);
  const nodeRefs = useRef({});
  const uploadRef = useRef(null);

  const size = PLATFORM_SIZES[platform];
  const maxW = 440, maxH = 560;
  const scale = Math.min(maxW / size.w, maxH / size.h);
  const stageW = Math.round(size.w * scale);
  const stageH = Math.round(size.h * scale);

  useEffect(() => {
    const weights = ["300 40px Montserrat", "400 40px Montserrat", "600 40px Montserrat", "700 40px Montserrat", "800 40px Montserrat", "900 40px Montserrat"];
    Promise.all(weights.map((f) => document.fonts.load(f))).then(() => setFontsReady(true)).catch(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    if (fontsReady && layerRef.current) layerRef.current.batchDraw();
  }, [fontsReady, doc, platform]);

  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const node = selectedId ? nodeRefs.current[selectedId] : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer() && tr.getLayer().batchDraw();
  }, [selectedId, doc, platform]);

  const selectedLayer = doc.layers.find((l) => l.id === selectedId) || null;

  const updateLayer = (id, patch) =>
    setDoc((d) => ({ ...d, layers: d.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
  const addLayer = (layer) => { setDoc((d) => ({ ...d, layers: [...d.layers, layer] })); setSelectedId(layer.id); };
  const removeLayer = (id) => { setDoc((d) => ({ ...d, layers: d.layers.filter((l) => l.id !== id) })); setSelectedId(null); };
  const moveLayer = (id, dir) =>
    setDoc((d) => {
      const idx = d.layers.findIndex((l) => l.id === id);
      const ni = idx + dir;
      if (idx < 0 || ni < 0 || ni >= d.layers.length) return d;
      const arr = [...d.layers];
      const [it] = arr.splice(idx, 1);
      arr.splice(ni, 0, it);
      return { ...d, layers: arr };
    });

  const applyPreset = (key) => { setDoc(buildPreset(key, b, size)); setSelectedId(null); };

  const changePlatform = (key) => {
    const ns = PLATFORM_SIZES[key], os = size;
    const rx = ns.w / os.w, ry = ns.h / os.h;
    setDoc((d) => ({ ...d, layers: d.layers.map((l) => rescaleLayer(l, rx, ry)) }));
    setPlatform(key);
  };

  const m = Math.round(size.w * 0.1);
  const addText = () => addLayer({ id: uid("text"), type: "text", text: "New text", x: m, y: Math.round(size.h * 0.4), width: size.w - 2 * m, fontSize: Math.round(size.w * 0.05), fontFamily: b.font, fontWeight: "700", fill: b.charcoal, align: "left", lineHeight: 1.2, letterSpacing: 0, opacity: 1 });
  const addRect = () => addLayer({ id: uid("rect"), type: "rect", x: m, y: Math.round(size.h * 0.4), width: Math.round(size.w * 0.4), height: Math.round(size.h * 0.15), fill: b.accent, cornerRadius: 0, opacity: 1, stroke: "", strokeWidth: 0 });
  const addCircle = () => addLayer({ id: uid("ell"), type: "ellipse", x: m, y: Math.round(size.h * 0.4), width: Math.round(size.w * 0.25), height: Math.round(size.w * 0.25), fill: b.accent, opacity: 1, stroke: "", strokeWidth: 0 });
  const addDivider = () => addLayer({ id: uid("div"), type: "rect", x: m, y: Math.round(size.h * 0.5), width: Math.round(size.w * 0.3), height: Math.max(3, Math.round(size.w * 0.006)), fill: b.accent, cornerRadius: 0, opacity: 1, stroke: "", strokeWidth: 0 });

  const openMediaPicker = async (target) => {
    setMediaTarget(target);
    try {
      const res = await api.get("/media");
      setMediaList(res.data);
    } catch { toast.error("Failed to load media"); }
    setMediaOpen(true);
  };

  const chooseMedia = (mItem) => {
    if (mediaTarget === "background") {
      setDoc((d) => ({ ...d, background: { type: "image", color: b.white, imageUrl: mItem.file_url } }));
    } else {
      addLayer({ id: uid("img"), type: "image", src: mItem.file_url, x: m, y: m, width: Math.round(size.w * 0.25), height: Math.round(size.w * 0.25), opacity: 1 });
    }
    setMediaOpen(false);
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("files", file);
    try {
      const res = await api.post("/media/upload?tags=upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mItem = res.data.media?.[0];
      if (mItem) chooseMedia({ file_url: mItem.file_url });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setMediaOpen(false);
    e.target.value = "";
  };

  const canvasJson = () => ({ ...doc, platform });

  const saveDesign = async (thumbUrl) => {
    const payload = { name, platform, preset: doc.preset || null, canvas_json: canvasJson(), thumbnail_url: thumbUrl || initialDesign?.thumbnail_url || null };
    if (designId) {
      const res = await api.put(`/designs/${designId}`, payload);
      return res.data;
    }
    const res = await api.post("/designs", payload);
    setDesignId(res.data.id);
    return res.data;
  };

  const handleSaveDesign = async () => {
    setSaving(true);
    try {
      await saveDesign(null);
      toast.success("Design saved");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    setSaving(true);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 80));
    try {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 1 / scale, mimeType: "image/png" });
      const res = await api.post("/media/upload-data-url", { data_url: dataUrl, filename: `${name.replace(/\s+/g, "-").toLowerCase()}.png`, tags: ["composed", doc.preset || "custom"] });
      const media = res.data;
      await saveDesign(media.file_url);
      setSavedMedia(media);
      onSaved && onSaved();
      toast.success("Exported to Media Library");
      setPostOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setSaving(false); }
  };

  const handleCreatePost = async () => {
    if (!postForm.caption.trim() || !postForm.scheduled_date) { toast.error("Add a caption and a date"); return; }
    setPostCreating(true);
    try {
      await api.post("/posts", {
        draft_text: postForm.caption,
        platform: postForm.platform,
        scheduled_date: new Date(postForm.scheduled_date).toISOString(),
        attached_media_id: savedMedia.id,
      });
      toast.success("Post added to calendar");
      setPostOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setPostCreating(false); }
  };

  const registerRef = (id) => (node) => { if (node) nodeRefs.current[id] = node; else delete nodeRefs.current[id]; };
  const enabledAnchors = selectedLayer?.type === "text"
    ? ["middle-left", "middle-right"]
    : ["top-left", "top-right", "bottom-left", "bottom-right", "middle-left", "middle-right", "top-center", "bottom-center"];

  const swatches = brandSwatches(b);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="canvas-editor">
      {/* Top toolbar */}
      <div className="flex items-center justify-between px-5 h-16 border-b border-[#e5e7eb] bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <Button data-testid="editor-close" onClick={onClose} variant="ghost" className="text-[#3C4048] hover:bg-[#f5f5f5] gap-1 px-2">
            <CaretLeft size={18} /> Back
          </Button>
          <Input data-testid="design-name" value={name} onChange={(e) => setName(e.target.value)} className="w-64 border-[#e5e7eb] text-[#3C4048]" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-[12px] border border-[#e5e7eb] overflow-hidden">
            {Object.entries(PLATFORM_SIZES).map(([k, v]) => (
              <button key={k} data-testid={`platform-${k.replace(":", "-")}`} onClick={() => changePlatform(k)}
                className={`px-3 py-2 text-xs font-semibold transition-colors ${platform === k ? "bg-[#5FA8B4] text-white" : "text-[#3C4048] hover:bg-[#f5f5f5]"}`}>
                {k}
              </button>
            ))}
          </div>
          <Button data-testid="save-design" onClick={handleSaveDesign} disabled={saving} variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] gap-2 rounded-[12px]">
            <FloppyDisk size={16} /> Save
          </Button>
          <Button data-testid="export-design" onClick={handleExport} disabled={saving} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
            <Export size={16} /> {saving ? "Working…" : "Export & Save"}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: presets, add tools, layers */}
        <div className="w-64 border-r border-[#e5e7eb] bg-[#fafafa] overflow-y-auto p-4 space-y-6">
          <div>
            <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-widest mb-2">Presets</p>
            <div className="space-y-2">
              {PRESETS.map((p) => (
                <button key={p.key} data-testid={`preset-${p.key}`} onClick={() => applyPreset(p.key)}
                  className={`w-full text-left p-3 rounded-[12px] border transition-colors ${doc.preset === p.key ? "border-[#5FA8B4] bg-[#f0f7f8]" : "border-[#e5e7eb] bg-white hover:border-[#5FA8B4]"}`}>
                  <p className="text-sm font-bold text-[#3C4048]">{p.name}</p>
                  <p className="text-xs text-[#7A9090] mt-0.5 leading-snug">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-widest mb-2">Add</p>
            <div className="grid grid-cols-2 gap-2">
              <ToolBtn testid="add-text" icon={TextT} label="Text" onClick={addText} />
              <ToolBtn testid="add-rect" icon={Square} label="Rectangle" onClick={addRect} />
              <ToolBtn testid="add-circle" icon={Circle} label="Circle" onClick={addCircle} />
              <ToolBtn testid="add-divider" icon={Minus} label="Divider" onClick={addDivider} />
              <ToolBtn testid="add-image" icon={ImageIcon} label="Image" onClick={() => openMediaPicker("overlay")} />
              <ToolBtn testid="set-background" icon={UploadSimple} label="Background" onClick={() => openMediaPicker("background")} />
            </div>
            <div className="mt-2 flex items-center gap-2">
              {swatches.slice(0, 6).map((c) => (
                <button key={c} title={c} onClick={() => setDoc((d) => ({ ...d, background: { type: "color", color: c, imageUrl: null } }))}
                  className="w-6 h-6 rounded-full border border-[#e5e7eb]" style={{ background: c }} data-testid={`bg-swatch-${c.replace("#", "")}`} />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[#7A9090] text-xs font-semibold uppercase tracking-widest mb-2">Layers</p>
            <div className="space-y-1">
              {[...doc.layers].reverse().map((l) => (
                <div key={l.id} onClick={() => setSelectedId(l.id)}
                  className={`flex items-center justify-between px-2 py-1.5 rounded-[8px] cursor-pointer text-sm ${selectedId === l.id ? "bg-[#5FA8B4] text-white" : "text-[#3C4048] hover:bg-[#f0f0f0]"}`}
                  data-testid={`layer-item-${l.id}`}>
                  <span className="truncate">{l.type === "text" ? (l.role === "logo" ? "Logo: " : "") + (l.text || "Text") : l.type === "image" ? "Image" : l.type === "ellipse" ? "Circle" : "Shape"}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); moveLayer(l.id, 1); }} className="opacity-70 hover:opacity-100"><ArrowUp size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); moveLayer(l.id, -1); }} className="opacity-70 hover:opacity-100"><ArrowDown size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); removeLayer(l.id); }} className="opacity-70 hover:opacity-100"><Trash size={13} /></button>
                  </div>
                </div>
              ))}
              {doc.layers.length === 0 && <p className="text-xs text-[#a3a3a3]">No layers yet.</p>}
            </div>
          </div>
        </div>

        {/* Center: canvas */}
        <div className="flex-1 flex flex-col items-center justify-center bg-[#eef1f2] p-6 overflow-auto">
          <p className="text-[#7A9090] text-xs font-semibold mb-3">{size.label} · {size.w}×{size.h}</p>
          <div className="border border-[#e5e7eb] shadow-sm" style={{ borderRadius: 0 }}>
            <Stage
              ref={stageRef}
              width={stageW}
              height={stageH}
              scaleX={scale}
              scaleY={scale}
              onMouseDown={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}
              onTouchStart={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}
            >
              <Layer ref={layerRef}>
                <Rect x={0} y={0} width={size.w} height={size.h} fill={doc.background?.color || b.white} listening={false} />
                {doc.background?.type === "image" && doc.background?.imageUrl && (
                  <BgImage url={doc.background.imageUrl} w={size.w} h={size.h} />
                )}
                {doc.layers.map((l) => {
                  if (l.type === "text") {
                    return (
                      <Text key={l.id} id={l.id} ref={registerRef(l.id)} text={l.text} x={l.x} y={l.y} width={l.width}
                        fontSize={l.fontSize} fontFamily={l.fontFamily || b.font} fontStyle={l.fontWeight || "400"}
                        fill={l.fill} align={l.align || "left"} lineHeight={l.lineHeight || 1.2} letterSpacing={l.letterSpacing || 0}
                        opacity={l.opacity ?? 1} draggable
                        onClick={() => setSelectedId(l.id)} onTap={() => setSelectedId(l.id)}
                        onDragEnd={(e) => updateLayer(l.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
                        onTransformEnd={(e) => { const node = e.target; const sx = node.scaleX(); node.scaleX(1); node.scaleY(1); updateLayer(l.id, { x: Math.round(node.x()), y: Math.round(node.y()), width: Math.max(20, Math.round(node.width() * sx)) }); }}
                      />
                    );
                  }
                  if (l.type === "rect") {
                    return (
                      <Rect key={l.id} id={l.id} ref={registerRef(l.id)} x={l.x} y={l.y} width={l.width} height={l.height}
                        fill={l.fill} cornerRadius={l.cornerRadius || 0} opacity={l.opacity ?? 1}
                        stroke={l.stroke || undefined} strokeWidth={l.strokeWidth || 0} draggable
                        onClick={() => setSelectedId(l.id)} onTap={() => setSelectedId(l.id)}
                        onDragEnd={(e) => updateLayer(l.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
                        onTransformEnd={(e) => { const node = e.target; const sx = node.scaleX(), sy = node.scaleY(); node.scaleX(1); node.scaleY(1); updateLayer(l.id, { x: Math.round(node.x()), y: Math.round(node.y()), width: Math.max(4, Math.round(node.width() * sx)), height: Math.max(2, Math.round(node.height() * sy)) }); }}
                      />
                    );
                  }
                  if (l.type === "ellipse") {
                    return (
                      <Group key={l.id} id={l.id} ref={registerRef(l.id)} x={l.x} y={l.y} draggable
                        onClick={() => setSelectedId(l.id)} onTap={() => setSelectedId(l.id)}
                        onDragEnd={(e) => updateLayer(l.id, { x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
                        onTransformEnd={(e) => { const node = e.target; const sx = node.scaleX(), sy = node.scaleY(); node.scaleX(1); node.scaleY(1); updateLayer(l.id, { x: Math.round(node.x()), y: Math.round(node.y()), width: Math.max(6, Math.round(l.width * sx)), height: Math.max(6, Math.round(l.height * sy)) }); }}
                      >
                        <Ellipse x={l.width / 2} y={l.height / 2} radiusX={l.width / 2} radiusY={l.height / 2} fill={l.fill} opacity={l.opacity ?? 1} stroke={l.stroke || undefined} strokeWidth={l.strokeWidth || 0} />
                      </Group>
                    );
                  }
                  if (l.type === "image") {
                    return <OverlayImage key={l.id} layer={l} registerRef={registerRef(l.id)} onSelect={() => setSelectedId(l.id)} onChange={(patch) => updateLayer(l.id, patch)} />;
                  }
                  return null;
                })}
                <Transformer ref={trRef} rotateEnabled={false} enabledAnchors={enabledAnchors} borderStroke="#5FA8B4" anchorStroke="#5FA8B4" anchorFill="#ffffff" anchorSize={8} />
              </Layer>
            </Stage>
          </div>
        </div>

        {/* Right: properties */}
        <div className="w-72 border-l border-[#e5e7eb] bg-[#fafafa] overflow-y-auto p-4" data-testid="properties-panel">
          {!selectedLayer && <p className="text-sm text-[#7A9090]">Select a layer to edit its properties.</p>}
          {selectedLayer && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[#3C4048] text-sm font-bold capitalize">{selectedLayer.type === "ellipse" ? "Circle" : selectedLayer.type}</p>
                <Button size="sm" variant="ghost" className="text-[#ef4444] h-7 px-2" onClick={() => removeLayer(selectedLayer.id)} data-testid="delete-layer"><Trash size={14} /></Button>
              </div>

              {selectedLayer.type === "text" && (
                <>
                  <Field label="Text">
                    <Textarea data-testid="prop-text" value={selectedLayer.text} onChange={(e) => updateLayer(selectedLayer.id, { text: e.target.value })} className="border-[#e5e7eb] text-[#3C4048] min-h-[70px]" />
                  </Field>
                  <Field label={`Size · ${selectedLayer.fontSize}px`}>
                    <input type="range" min="10" max={Math.round(size.w * 0.22)} value={selectedLayer.fontSize} onChange={(e) => updateLayer(selectedLayer.id, { fontSize: parseInt(e.target.value) })} className="w-full accent-[#5FA8B4]" data-testid="prop-fontsize" />
                  </Field>
                  <Field label="Weight">
                    <div className="grid grid-cols-3 gap-1">
                      {FONT_WEIGHTS.map((fw) => (
                        <button key={fw.v} onClick={() => updateLayer(selectedLayer.id, { fontWeight: fw.v })}
                          className={`text-[11px] py-1 rounded-[8px] border ${selectedLayer.fontWeight === fw.v ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb]"}`}>{fw.label}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Align">
                    <div className="flex gap-1">
                      {["left", "center", "right"].map((a) => (
                        <button key={a} onClick={() => updateLayer(selectedLayer.id, { align: a })}
                          className={`flex-1 text-xs py-1.5 rounded-[8px] border capitalize ${selectedLayer.align === a ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb]"}`}>{a}</button>
                      ))}
                    </div>
                  </Field>
                  <ColorField label="Colour" value={selectedLayer.fill} swatches={swatches} onChange={(c) => updateLayer(selectedLayer.id, { fill: c })} />
                </>
              )}

              {(selectedLayer.type === "rect" || selectedLayer.type === "ellipse") && (
                <>
                  <ColorField label="Fill" value={selectedLayer.fill} swatches={swatches} onChange={(c) => updateLayer(selectedLayer.id, { fill: c })} />
                  {selectedLayer.type === "rect" && (
                    <Field label={`Corner radius · ${selectedLayer.cornerRadius || 0}`}>
                      <input type="range" min="0" max={Math.round(size.w * 0.1)} value={selectedLayer.cornerRadius || 0} onChange={(e) => updateLayer(selectedLayer.id, { cornerRadius: parseInt(e.target.value) })} className="w-full accent-[#5FA8B4]" />
                    </Field>
                  )}
                </>
              )}

              <Field label={`Opacity · ${Math.round((selectedLayer.opacity ?? 1) * 100)}%`}>
                <input type="range" min="0" max="100" value={Math.round((selectedLayer.opacity ?? 1) * 100)} onChange={(e) => updateLayer(selectedLayer.id, { opacity: parseInt(e.target.value) / 100 })} className="w-full accent-[#5FA8B4]" data-testid="prop-opacity" />
              </Field>
            </div>
          )}
        </div>
      </div>

      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={handleUploadFile} />

      {/* Media picker dialog */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#3C4048]">Choose {mediaTarget === "background" ? "background" : "image"}</DialogTitle>
          </DialogHeader>
          <Button onClick={() => uploadRef.current?.click()} variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] gap-2 rounded-[12px] w-fit" data-testid="upload-from-picker">
            <UploadSimple size={16} /> Upload new image
          </Button>
          <div className="grid grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto mt-2">
            {mediaList.map((mItem) => (
              <button key={mItem.id} onClick={() => chooseMedia(mItem)} className="aspect-square overflow-hidden rounded-[8px] border border-[#e5e7eb] hover:border-[#5FA8B4]" data-testid={`pick-media-${mItem.id}`}>
                <img src={mItem.file_url} alt={mItem.filename} className="w-full h-full object-cover" />
              </button>
            ))}
            {mediaList.length === 0 && <p className="text-sm text-[#7A9090] col-span-4">No media yet — upload one above.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Post from composition dialog */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#3C4048]">Saved! Create a post?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#7A9090] -mt-2">Your composition is in the Media Library. Optionally send it straight to the calendar.</p>
          {savedMedia && <img src={savedMedia.file_url} alt="composition" className="w-full max-h-52 object-contain border border-[#e5e7eb] rounded-[8px] bg-[#f5f5f5]" />}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Platform">
              <select value={postForm.platform} onChange={(e) => setPostForm((f) => ({ ...f, platform: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[8px] p-2 text-sm text-[#3C4048]" data-testid="post-platform">
                {["instagram", "facebook", "linkedin", "tiktok", "youtube"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Scheduled date">
              <input type="datetime-local" value={postForm.scheduled_date} onChange={(e) => setPostForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[8px] p-2 text-sm text-[#3C4048]" data-testid="post-date" />
            </Field>
          </div>
          <Field label="Caption">
            <Textarea value={postForm.caption} onChange={(e) => setPostForm((f) => ({ ...f, caption: e.target.value }))} placeholder={"Hook…\n\nAgitate…\n\nSolution…\n\nProof…\n\nCTA…"} className="border-[#e5e7eb] text-[#3C4048] min-h-[110px] whitespace-pre-line" data-testid="post-caption" />
          </Field>
          <div className="flex gap-3">
            <Button onClick={handleCreatePost} disabled={postCreating} className="flex-1 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[12px]" data-testid="post-submit">{postCreating ? "Adding…" : "Add to Calendar"}</Button>
            <Button onClick={() => setPostOpen(false)} variant="outline" className="border-[#e5e7eb] text-[#3C4048] rounded-[12px]">Skip</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 py-2.5 rounded-[12px] border border-[#e5e7eb] bg-white hover:border-[#5FA8B4] hover:bg-[#f0f7f8] transition-colors">
      <Icon size={18} className="text-[#3A7A8C]" />
      <span className="text-[11px] text-[#3C4048] font-medium">{label}</span>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function ColorField({ label, value, swatches, onChange }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2 flex-wrap">
        {swatches.map((c) => (
          <button key={c} onClick={() => onChange(c)} title={c}
            className={`w-7 h-7 rounded-full border-2 ${value?.toLowerCase() === c.toLowerCase() ? "border-[#3C4048]" : "border-[#e5e7eb]"}`}
            style={{ background: c }} />
        ))}
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-7 h-7 rounded cursor-pointer border border-[#e5e7eb] bg-white" title="Custom colour" />
      </div>
    </Field>
  );
}
